import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../../components/ui/dialog";
import { Button } from "../../../../../components/ui/button";
import { useAuth } from "../../../../../contexts/authContext";
import { getExternalControlUrl } from "../../../../../lib/systemOfRecord";
import { useOptroSampleImport } from "../../../../../hooks/useOptroSampleImport";
import { SyncedControlHeader } from "./SyncedControlHeader";
import { OptroTestCycleList } from "./OptroTestCycleList";
import { PreviewRowsTable } from "../sampleUpload/PreviewRowsTable";
import type { AuditSession } from "../../../../../models/audit/auditSession";
import type { ControlSessionEnvelope } from "../../../../../models/audit/controlSession";
import type {
  OptroTestCycle,
  OptroGridResponse,
  ParsedColumn,
  ColumnSelection,
  ColumnAttributeMapping,
} from "../../../../../models/audit/optroSampleImport";

type Step = "select" | "confirm";

interface PullSamplesFromOptroModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  auditSession: AuditSession;
  controlSession: ControlSessionEnvelope;
  onSuccess: () => void;
}

export function PullSamplesFromOptroModal({
  open,
  onOpenChange,
  auditSession,
  controlSession,
  onSuccess,
}: PullSamplesFromOptroModalProps) {
  const { workspace } = useAuth();
  const {
    listLoading,
    gridLoading,
    confirmLoading,
    listTestCycles,
    getGrid,
    confirmFirstUpload,
    confirmSubsequentUpload,
  } = useOptroSampleImport({ sessionId: auditSession.id });

  // Null for work-step-headed sessions — this modal is only ever opened from
  // the Optro entry points, which are gated on `control.externalRef`, so the
  // guards below (and the null render at the bottom) are belt-and-braces.
  const control = controlSession.control;
  const controlId = control?.id ?? null;
  const externalControlUrl =
    workspace?.systemOfRecord && control?.externalRef
      ? getExternalControlUrl(
          workspace.systemOfRecord,
          control.externalRef.externalRef,
        )
      : null;

  const [step, setStep] = useState<Step>("select");
  const [testCycles, setTestCycles] = useState<OptroTestCycle[]>([]);
  const [selectedTestCycle, setSelectedTestCycle] =
    useState<OptroTestCycle | null>(null);
  const [grid, setGrid] = useState<OptroGridResponse | null>(null);
  const [editedColumns, setEditedColumns] = useState<ParsedColumn[]>([]);
  const [matchColumnIndices, setMatchColumnIndices] = useState<Set<number>>(
    new Set(),
  );
  const [displayColumnIndices, setDisplayColumnIndices] = useState<Set<number>>(
    new Set(),
  );
  const [columnMappings, setColumnMappings] = useState<
    Map<number, string | null>
  >(new Map());

  // Guards the one-shot test-cycle fetch per control while the modal is open.
  const listedForControlRef = useRef<string | null>(null);

  const existingAttributes = useMemo(
    () => auditSession.procedure?.attributes ?? [],
    [auditSession.procedure?.attributes],
  );

  const resetState = useCallback(() => {
    setStep("select");
    setSelectedTestCycle(null);
    setGrid(null);
    setEditedColumns([]);
    setMatchColumnIndices(new Set());
    setDisplayColumnIndices(new Set());
    setColumnMappings(new Map());
  }, []);

  // Fetch the control's test cycles once when the modal opens.
  useEffect(() => {
    if (!open) {
      listedForControlRef.current = null;
      return;
    }
    if (!controlId) return;
    if (listedForControlRef.current === controlId) return;
    listedForControlRef.current = controlId;
    let cancelled = false;
    void (async () => {
      const result = await listTestCycles(controlId);
      if (!cancelled && result) {
        setTestCycles(result);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, controlId, listTestCycles]);

  const handleClose = useCallback(() => {
    resetState();
    setTestCycles([]);
    onOpenChange(false);
  }, [onOpenChange, resetState]);

  // Populate the editable column state from a grid response (mirrors the
  // CSV/Excel flow's init logic).
  const initColumnsFromGrid = useCallback(
    (gridResponse: OptroGridResponse) => {
      setEditedColumns(gridResponse.columns.map((col) => ({ ...col })));

      const allColumnIndices = new Set(
        gridResponse.columns.map((col) => col.columnIndex),
      );
      setDisplayColumnIndices(allColumnIndices);

      if (gridResponse.isFirstUpload) {
        const uniqueIdColumn = gridResponse.columns.find(
          (col) => col.detectedType === "unique_id",
        );
        const initialIndex =
          uniqueIdColumn?.columnIndex ??
          gridResponse.columns[0]?.columnIndex ??
          0;
        setMatchColumnIndices(new Set([initialIndex]));
      } else {
        const displayAttributes = existingAttributes.filter(
          (attr) => attr.sourceRowDisplay,
        );
        const initialMappings = new Map<number, string | null>();
        for (const col of gridResponse.columns) {
          const matchingAttr = displayAttributes.find(
            (attr) => attr.name.toLowerCase() === col.columnName.toLowerCase(),
          );
          initialMappings.set(col.columnIndex, matchingAttr?.id ?? null);
        }
        setColumnMappings(initialMappings);
      }
    },
    [existingAttributes],
  );

  const handleSelect = useCallback((testCycle: OptroTestCycle) => {
    setSelectedTestCycle(testCycle);
  }, []);

  // Continue: fetch the chosen cycle's grid, then advance to the confirm step.
  const handleContinue = useCallback(async () => {
    if (!controlId) return;
    if (!selectedTestCycle || selectedTestCycle.sampleCount <= 0) return;
    const gridResponse = await getGrid(controlId, selectedTestCycle.id);
    if (!gridResponse) return;
    setGrid(gridResponse);
    initColumnsFromGrid(gridResponse);
    setStep("confirm");
  }, [selectedTestCycle, controlId, getGrid, initColumnsFromGrid]);

  const handleChooseDifferentCycle = useCallback(() => {
    setStep("select");
    setGrid(null);
    setEditedColumns([]);
    setMatchColumnIndices(new Set());
    setDisplayColumnIndices(new Set());
    setColumnMappings(new Map());
  }, []);

  // NOTE: the "Detected columns" configuration UI is intentionally omitted for the
  // Optro pull — the grid columns are imported as detected (no per-column editing).
  // `editedColumns` / `matchColumnIndices` / `displayColumnIndices` are still
  // initialised with sensible defaults by `initColumnsFromGrid` and drive the
  // create payload below. (The CSV/Excel flow keeps its DetectedColumnsTable.)

  const hasValidMappings = useMemo(() => {
    for (const attrId of columnMappings.values()) {
      if (attrId !== null) return true;
    }
    return false;
  }, [columnMappings]);

  const isFirstUpload = grid?.isFirstUpload ?? true;

  const handleConfirm = useCallback(async () => {
    if (!grid || !selectedTestCycle) return;

    let result;
    if (grid.isFirstUpload) {
      if (matchColumnIndices.size === 0) return;
      const columnSelections: ColumnSelection[] = editedColumns.map(
        (column) => ({
          column_index: column.columnIndex,
          include: true,
          attribute_type: column.detectedType,
          attribute_name: column.columnName,
          source_row_display: displayColumnIndices.has(column.columnIndex),
        }),
      );
      result = await confirmFirstUpload(selectedTestCycle.id, columnSelections);
    } else {
      if (!hasValidMappings) return;
      const mappings: ColumnAttributeMapping[] = [];
      for (const [columnIndex, attributeId] of columnMappings.entries()) {
        if (attributeId !== null) {
          mappings.push({
            column_index: columnIndex,
            procedure_attribute_id: attributeId,
          });
        }
      }
      result = await confirmSubsequentUpload(selectedTestCycle.id, mappings);
    }

    if (result && result.status !== "error") {
      toast.success(
        `Samples added — ${result.createdSampleCount} samples added from Optro`,
      );
      handleClose();
      onSuccess();
    }
  }, [
    grid,
    selectedTestCycle,
    editedColumns,
    matchColumnIndices,
    displayColumnIndices,
    columnMappings,
    hasValidMappings,
    confirmFirstUpload,
    confirmSubsequentUpload,
    handleClose,
    onSuccess,
  ]);

  const canContinue = Boolean(
    selectedTestCycle && selectedTestCycle.sampleCount > 0,
  );

  const canConfirm = isFirstUpload
    ? matchColumnIndices.size > 0
    : hasValidMappings;

  // No linked control (work-step-headed session): the Optro entry points are
  // hidden, so this modal should never be open — render nothing if it is.
  // Placed after every hook call to keep the hook order stable.
  if (!control) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="flex max-h-[90vh] max-w-4xl flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Pull Samples from Optro</DialogTitle>
          <DialogDescription>
            {step === "select"
              ? "Select a test cycle to pull samples from"
              : "Review the samples to import from this test cycle"}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto py-4">
          <SyncedControlHeader
            controlName={control.name}
            externalControlUrl={externalControlUrl}
          />

          {step === "select" && (
            <OptroTestCycleList
              testCycles={testCycles}
              loading={listLoading}
              selectedTestCycleId={selectedTestCycle?.id ?? null}
              onSelect={handleSelect}
            />
          )}

          {step === "confirm" && grid && (
            <div className="space-y-6">
              <div className="flex items-center justify-between text-sm text-stone-600">
                <span>
                  From Optro:{" "}
                  <span className="font-medium">{selectedTestCycle?.name}</span>
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleChooseDifferentCycle}
                  disabled={confirmLoading}
                >
                  Choose a different cycle
                </Button>
              </div>

              <PreviewRowsTable
                columns={editedColumns}
                previewRows={grid.previewRows}
                totalRowCount={grid.totalRowCount}
              />
            </div>
          )}
        </div>

        <DialogFooter className="flex-shrink-0">
          <Button
            variant="ghost"
            onClick={handleClose}
            disabled={confirmLoading}
          >
            Cancel
          </Button>
          {step === "select" ? (
            <Button
              onClick={handleContinue}
              disabled={!canContinue || gridLoading}
              loading={gridLoading}
            >
              Continue
            </Button>
          ) : (
            <Button
              onClick={handleConfirm}
              disabled={confirmLoading || !canConfirm}
              loading={confirmLoading}
            >
              Add Samples
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
